/**
 * LIFF session hook.
 *
 * Status:
 *   loading      — กำลัง init liff
 *   needs_login  — init แล้ว แต่ user ยังไม่ login → แสดงปุ่ม Login
 *   ready        — login แล้ว มี profile + idToken (ตรวจ exp ก่อน)
 *   error        — init/profile fail
 */
import { useEffect, useState, useCallback } from 'react';
import { initLiff, liff, getProfile } from '../lib/liff';
import { isImpersonating, endImpersonation } from '../lib/impersonation';

export type LiffStatus = 'loading' | 'needs_login' | 'ready' | 'error';

export interface LiffProfile {
  userId: string;
  displayName: string;
  pictureUrl: string | null;
  statusMessage: string | null;
}

let cached: { status: LiffStatus; profile: LiffProfile | null } = {
  status: 'loading',
  profile: null,
};

function isTokenValid(): boolean {
  try {
    const decoded = liff.getDecodedIDToken();
    if (!decoded || typeof decoded.exp !== 'number') return false;
    // Consider expired if less than 60s remaining
    return decoded.exp * 1000 - Date.now() > 60_000;
  } catch {
    return false;
  }
}

export function useLiffSession() {
  const [state, setState] = useState(cached);

  useEffect(() => {
    let cancelled = false;
    if (state.status === 'ready') return;
    (async () => {
      try {
        // ── Admin impersonation path — skip LIFF init entirely ──────
        // API calls will use `AdminImp <jwt>` header; profile info comes
        // from a stub since we're not really a LINE session.
        if (isImpersonating()) {
          cached = {
            status: 'ready',
            profile: {
              userId: 'ADMIN_IMPERSONATION',
              displayName: 'Admin Impersonation',
              pictureUrl: null,
              statusMessage: null,
            },
          };
          setState(cached);
          return;
        }

        await initLiff();
        if (cancelled) return;

        if (!liff.isLoggedIn()) {
          cached = { status: 'needs_login', profile: null };
          setState(cached);
          return;
        }

        // ตรวจ token ว่ายังไม่หมดอายุ
        if (!isTokenValid()) {
          console.warn('[LIFF] idToken expired or invalid — clearing session');
          liff.logout();
          cached = { status: 'needs_login', profile: null };
          setState(cached);
          return;
        }

        const p = await getProfile();
        if (cancelled) return;
        cached = {
          status: 'ready',
          profile: {
            userId: p.userId,
            displayName: p.displayName,
            pictureUrl: p.pictureUrl ?? null,
            statusMessage: p.statusMessage ?? null,
          },
        };
        setState(cached);
      } catch (err) {
        console.error('[LIFF init]', err);
        if (!cancelled) {
          cached = { status: 'error', profile: null };
          setState(cached);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(() => {
    liff.login({ redirectUri: window.location.href });
  }, []);

  const logout = useCallback(() => {
    if (isImpersonating()) {
      endImpersonation();
      return;
    }
    liff.logout();
    window.location.href = '/';
  }, []);

  return { ...state, login, logout };
}
